import {LabelShowManager} from './LabelShowManager';
import {Easing, Meter, Timer, RichText} from '@gongxigames/framework/core';
import type {EventDataWinMeterCountUp, EventDataWinMeterShowingFinalValue, EventDataWinMeterSetValue} from '@gongxigames/framework/slotV2/types';
import {uiMainScreenEmitter} from '../../UIMainScreenEmitter';
import {slotStore} from '@gongxigames/framework/slotV2/globals';

export type CallBack = () => void;

export const METER_TEXT_FIELD_ID = 'meter';
export const SHOWING_FINAL_VALUE_DELAY = 500;

export class WinMeterManager {
    private meter!: Meter;
    private finalValueShowTimer?: Timer;

    private readonly currencyFormatter = slotStore.currencyFormatter.value!;
    private readonly updateManager = slotStore.updateManager.value!;

    constructor(
        readonly labelShowManager: LabelShowManager,
        text: RichText,
    ) {
        uiMainScreenEmitter.on('COUNT_UP', this.countUp.bind(this));
        uiMainScreenEmitter.on('STOP_AFTER_SHOWING_FINAL_VALUE', this.stopAfterShowingFinalValue.bind(this));
        uiMainScreenEmitter.on('STOP_FINAL_VALUE_TIMER', this.stopFinalValueTimer.bind(this));
        uiMainScreenEmitter.on('SET_VALUE', this.setValue.bind(this));
        uiMainScreenEmitter.on('SNAP_TO_FINAL_VALUE', this.snapToFinalValue.bind(this));
        uiMainScreenEmitter.on('STOP', this.hideWinMeter.bind(this));

        this.setMeterTextObject(text);
    }

    public setValue(options: EventDataWinMeterSetValue): void {
        if (options.setIfPlaying) {
            if (this.meter.isPlaying) {
                this.meter.set(options.value);
                this.labelShowManager.show(METER_TEXT_FIELD_ID);
            }
        } else {
            this.meter.set(options.value);
            this.labelShowManager.show(METER_TEXT_FIELD_ID);
        }
    }

    public stopAfterShowingFinalValue(options: EventDataWinMeterShowingFinalValue): void {
        if (this.meter.isPlaying) {
            this.meter.set(options.value);
            this.stopFinalValueTimer();

            if (!options.clearWithDelay) {
                this.finalValueShowTimer = new Timer(this.hideWinMeter.bind(this), SHOWING_FINAL_VALUE_DELAY, true);
                this.updateManager.add(this.finalValueShowTimer);
            }
        } else {
            this.hideWinMeter();
        }
    }

    public stopFinalValueTimer(): void {
        if (this.finalValueShowTimer) {
            this.updateManager.remove(this.finalValueShowTimer);
            this.finalValueShowTimer.stop();
            delete this.finalValueShowTimer;
        }
    }

    public snapToFinalValue(): void {
        this.meter.snapToFinalValue();
    }

    public setValueIfPlaying(value: number): void {
        if (this.meter.isPlaying) {
            this.meter.set(value);
        }
    }

    public hideWinMeter(): void {
        this.labelShowManager.hide(METER_TEXT_FIELD_ID);
    }

    public async countUp(data: EventDataWinMeterCountUp): Promise<void> {
        this.labelShowManager.show(METER_TEXT_FIELD_ID);
        this.meter.from(data.from).to(data.to, data.duration);

        return new Promise(res => {
            this.meter.onCompleteCallback = res;
        });
    }

    public setMeterTextObject(text: RichText): void {
        if (this.meter) {
            this.meter.stop();
        }

        this.meter = new Meter(text, Easing.Linear.None, this.currencyFormatter);
        this.meter.start();
        this.labelShowManager.hide(METER_TEXT_FIELD_ID);
        this.updateManager.add(this.meter);
    }
}